<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { BadgeProps } from '#layers/ui/components/Badge.vue';
import type { ThemeSlots } from '#layers/ui/types/tv';
import { tv } from 'tailwind-variants';

const separator = tv({
	slots: {
		root: 'col-span-full flex flex-col',
		label: '',
		description: 'text-dimmed',
		badgeSize: '',
	},
	variants: {
		size: {
			md: {
				root: 'gap-y-4',
				label: 'font-semibold',
				description: 'text-sm',
				// eslint-disable-next-line better-tailwindcss/no-unknown-classes
				badgeSize: 'md',
			},
			lg: {
				root: 'gap-y-5',
				label: 'text-lg font-semibold',
				// eslint-disable-next-line better-tailwindcss/no-unknown-classes
				badgeSize: 'md',
			},
		},
	},
	defaultVariants: {
		size: 'md',
	},
});

export interface FormSeparatorProps {
	class?: string;
	label?: string;
	description?: string;
	line?: false | 'above' | 'below';
	size?: SeparatorVariants['size'];
	badge?: BadgeProps;
	ui?: ThemeSlots<typeof separator>;
}

export interface FormSeparatorSlots {
	default?: () => void;
	description?: () => void;
	trailing?: () => void;
}

type SeparatorVariants = VariantProps<typeof separator>;
</script>

<script lang="ts" setup>
import { computed } from 'vue';

const props = withDefaults(defineProps<FormSeparatorProps>(), {
	line: 'below',
});

defineSlots<FormSeparatorSlots>();

const ui = computed(() => separator({ size: props.size }));
</script>

<template>
	<div :class="ui.root({ class: [props.ui?.root, props.class] })">
		<DSeparator v-if="line === 'above'" />
		<div
			v-if="label || description || badge || !!$slots.default || !!$slots.description || !!$slots.trailing"
			class="flex w-full items-center justify-between"
		>
			<div>
				<p
					v-if="label || !!$slots.default"
					:class="ui.label({ class: [props.ui?.label] })"
				>
					<slot>
						{{ label }}
					</slot>
				</p>
				<p
					v-if="description || !!$slots.description"
					:class="ui.description({ class: [props.ui?.description] })"
				>
					<slot name="description">
						{{ description }}
					</slot>
				</p>
			</div>
			<slot name="trailing">
				<DBadge
					v-if="badge"
					:size="ui.badgeSize({ class: [props.ui?.badgeSize] }) as BadgeProps['size']"
					v-bind="badge"
				/>
			</slot>
		</div>
		<DSeparator v-if="line === 'below'" />
	</div>
</template>
